import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { Section } from "@/components/sections";
import { Breadcrumbs } from "@/components/sections";
import LeadForm from "@/components/LeadForm";
import { CONTACT } from "@/constants/contact";
import { fetchPost, formatPostDate, portableTextToBlocks } from "@/lib/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let live = true;
    setState("loading");
    fetchPost(slug)
      .then((data) => {
        if (!live) return;
        if (!data) {
          setState("missing");
          return;
        }
        setPost(data);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [slug]);

  if (state !== "ready") {
    return (
      <div>
        <SEO path={`/blog/${slug}`} title="Insights" description="An article from Rao Investment Solutions." />
        <Section>
          <p className="font-sans text-base text-slate">
            {state === "loading" && "Loading…"}
            {state === "missing" && "That post could not be found. It may have been moved or unpublished."}
            {state === "error" && "This post could not be loaded right now. Please try again shortly."}
          </p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-sans text-sm text-orange hover:underline">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Insights
          </Link>
        </Section>
      </div>
    );
  }

  const blocks = portableTextToBlocks(post.body);

  return (
    <div>
      <SEO
        path={`/blog/${post.slug}`}
        title={post.title}
        description={post.excerpt || `An article from ${CONTACT.brand}.`}
      />

      <Section className="!pb-0">
        <div className="max-w-3xl">
          <Breadcrumbs
            items={[{ name: "Home", to: "/" }, { name: "Insights", to: "/blog" }, { name: post.title }]}
          />
          <div className="mt-8 flex flex-wrap items-center gap-3 font-data text-xs text-slate">
            {post.category && <span className="uppercase tracking-[0.18em] text-orange">{post.category}</span>}
            {post.publishedAt && <span>{formatPostDate(post.publishedAt)}</span>}
            {post.readingTime > 0 && <span>· {post.readingTime} min read</span>}
          </div>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-navy tracking-tight">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-5 font-sans text-lg text-slate leading-relaxed">{post.excerpt}</p>
          )}
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl">
          {blocks.map((node) =>
            node.kind === "list" ? (
              <ListBlock key={node.key} node={node} />
            ) : (
              <TextBlock key={node.key} node={node} />
            )
          )}

          <div className="mt-14 pt-8 border-t border-navy/10">
            <p className="font-data text-xs text-slate leading-relaxed">
              This article is for information only and is not investment advice or a recommendation to
              buy or sell any security. Mutual fund investments are subject to market risks. Read all
              scheme related documents carefully before investing. {CONTACT.brand} is an AMFI-registered
              Mutual Fund Distributor ({CONTACT.arn}).
            </p>
          </div>

          <Link
            to="/blog"
            className="mt-10 inline-flex items-center gap-2 font-sans text-sm text-navy hover:text-orange transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Insights
          </Link>
        </div>
      </Section>

      <Section dark>
        <div className="max-w-3xl">
          <LeadForm dark title="Discuss this with an advisor" />
        </div>
      </Section>
    </div>
  );
}

const spanClasses = (marks) => {
  const cls = [];
  if (marks.includes("strong")) cls.push("font-medium text-navy");
  if (marks.includes("em")) cls.push("italic");
  if (marks.includes("code")) cls.push("font-data text-sm bg-cream px-1.5 py-0.5");
  return cls.join(" ");
};

const renderSpans = (spans, markDefs) =>
  spans.map((span, i) => {
    const linkMark = span.marks.find((m) => markDefs.some((d) => d._key === m && d._type === "link"));
    const content = (
      <span key={`s${i}`} className={spanClasses(span.marks)}>
        {span.text}
      </span>
    );
    if (!linkMark) return content;
    const def = markDefs.find((d) => d._key === linkMark);
    return (
      <a
        key={`s${i}`}
        href={def.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange underline underline-offset-2 hover:no-underline"
      >
        {span.text}
      </a>
    );
  });

const TextBlock = ({ node }) => {
  const children = renderSpans(node.spans, node.markDefs);
  switch (node.style) {
    case "h2":
      return <h2 className="mt-12 mb-4 font-serif text-2xl md:text-3xl text-navy tracking-tight">{children}</h2>;
    case "h3":
      return <h3 className="mt-10 mb-3 font-serif text-xl md:text-2xl text-navy tracking-tight">{children}</h3>;
    case "h4":
      return <h4 className="mt-8 mb-3 font-sans text-base font-medium text-navy">{children}</h4>;
    case "blockquote":
      return (
        <blockquote className="my-8 border-l-2 border-orange pl-6 font-serif text-xl text-navy leading-relaxed">
          {children}
        </blockquote>
      );
    default:
      return <p className="mb-5 font-sans text-lg text-slate leading-relaxed">{children}</p>;
  }
};

const ListBlock = ({ node }) => {
  const Tag = node.type;
  return (
    <Tag
      className={`mb-6 pl-6 space-y-2 font-sans text-lg text-slate leading-relaxed ${
        node.type === "ol" ? "list-decimal" : "list-disc"
      }`}
    >
      {node.items.map((item) => (
        <li key={item.key}>{renderSpans(item.spans, item.markDefs)}</li>
      ))}
    </Tag>
  );
};
